import {
  createPurchaseByBidApi,
  createPurchaseCartApi,
  createPurchaseFileApi,
  createPurchaseReRequestApi,
  deletePurchaseApi,
  getPurchaseApi,
  getPurchaseDetailApi,
  getPurchaseItemApi,
} from '@apis/purchase';
import { Toast } from '@components/_atoms/Toast';
import { AxiosError } from 'axios';
import {
  createPurchaseReRequestPayload,
  deletePurchasePayload,
  getPurchaseDetailPayload,
  getPurchaseItemPayload,
  getPurchasePayload,
} from 'models/Supplement';
import {
  QuotationPurchaseGetPurchaseQuotationSelfError,
  QuotationPurchasePostPurchaseQuotationByCartError,
  QuotationPurchasePostPurchaseQuotationByCartPayload,
  QuotationPurchasePostPurchaseQuotationByFileError,
  QuotationPurchasePostPurchaseQuotationByFilePayload,
  QuotationPurchaseSelfDeleteError,
  QuotationPurchaseSelfPostPurchaseQuotationByBiddingError,
  QuotationPurchaseSelfPostPurchaseQuotationReRequestError,
  QuotationPurchaseSelfReadError,
} from 'models/swagger/data-contracts';
import { useMutation, useQuery } from 'react-query';

/**
 *  구매자 - 구매 견적 요청서 생성 - 파일 Fetch
 *  @function useCreatePurchaseFileFetch
 */
export const useCreatePurchaseFileFetch = () => {
  const { mutate: createPurchaseFileMutate, isLoading: createPurchaseFileIsLoading } = useMutation(
    ['createPurchaseFile'],
    (purhcaseFilePayload: QuotationPurchasePostPurchaseQuotationByFilePayload) =>
      createPurchaseFileApi(purhcaseFilePayload),
    {
      onError: (err: AxiosError<QuotationPurchasePostPurchaseQuotationByFileError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    createPurchaseFileMutate,
    createPurchaseFileIsLoading,
  };
};

/**
 *  구매자 - 구매 견적 요청서 생성 - 카트 Fetch
 *  @function useCreatePurchaseCartFetch
 */
export const useCreatePurchaseCartFetch = () => {
  const { mutate: createPurchaseCartMutate, isLoading: createPurchaseCartIsLoading } = useMutation(
    ['createPurchaseCart'],
    (purchaseCartPayload: QuotationPurchasePostPurchaseQuotationByCartPayload) =>
      createPurchaseCartApi(purchaseCartPayload),
    {
      onError: (err: AxiosError<QuotationPurchasePostPurchaseQuotationByCartError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    createPurchaseCartMutate,
    createPurchaseCartIsLoading,
  };
};

/**
 *  구매자 - 자신의 구매 견적 요청서 리스트 가져오기 Fetch
 *  @function useGetPurchaseFetch
 */
export const useGetPurchaseFetch = ({ page, kind, finish, project_name, rfq_number, sub_kind }: getPurchasePayload) => {
  const {
    data: getPurchaseFetchData,
    isLoading: getPurchaseIsLoading,
    isError: getPurchaseIsError,
  } = useQuery(
    ['getPurchase', page, kind, sub_kind, finish, project_name, rfq_number],
    async () => {
      const result = await getPurchaseApi({
        page,
        kind,
        sub_kind,
        finish,
        project_name,
        rfq_number,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err: AxiosError<QuotationPurchaseGetPurchaseQuotationSelfError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getPurchaseFetchData || getPurchaseIsLoading) && !getPurchaseIsError;

  return {
    getPurchaseFetchData,
    isCustomLoading,
  };
};

/**
 *  구매자 - 자신의 구매 견적 요청서 상세 조회 Fetch
 *  @function useGetPurchaseDetailFetch
 */
export const useGetPurchaseDetailFetch = ({ rfq_number_id }: getPurchaseDetailPayload) => {
  const {
    data: getPurchaseDetailFetchData,
    isLoading: getPurchaseDetailIsLoading,
    isError: getPurchaseDetailIsError,
  } = useQuery(
    ['getPurchaseDetail', rfq_number_id],
    async () => {
      const result = await getPurchaseDetailApi({
        rfq_number_id,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!rfq_number_id,
      onError: (err: AxiosError<QuotationPurchaseSelfReadError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getPurchaseDetailFetchData || getPurchaseDetailIsLoading) && !getPurchaseDetailIsError;

  return {
    getPurchaseDetailFetchData,
    isCustomLoading,
  };
};

/**
 *  구매자 - 자신의 구매 견적 요청서 자재 리스트 조회 Fetch
 *  @function useGetPurchaseItemFetch
 */
export const useGetPurchaseItemFetch = ({ rfq_number_id }: getPurchaseItemPayload) => {
  const {
    data: getPurchaseItemFetchData,
    isLoading: getPurchaseItemIsLoading,
    isError: getPurchaseItemIsError,
  } = useQuery(
    ['getPurchaseItem', rfq_number_id],
    async () => {
      const result = await getPurchaseItemApi({
        rfq_number_id,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!rfq_number_id,
      onError: (err: AxiosError<QuotationPurchaseSelfDeleteError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  const isCustomLoading = (!getPurchaseItemFetchData || getPurchaseItemIsLoading) && !getPurchaseItemIsError;

  return {
    getPurchaseItemFetchData,
    isCustomLoading,
  };
};

/**
 *  구매자 - 수정요청된 견적서 재요청 Fetch
 *  @function useCreatePurchaseReRequestFetch
 */
export const useCreatePurchaseReRequestFetch = () => {
  // TODO : isLoading을 이렇게 길게 가야하는 이유..?
  const { mutate: createPurchaseReRequestMutate, isLoading: createPurchaseReRequestIsLoading } = useMutation(
    ['createPurchaseReRequest'],
    (purchaseReRequestPayload: createPurchaseReRequestPayload) => createPurchaseReRequestApi(purchaseReRequestPayload),
    {
      onError: (err: AxiosError<QuotationPurchaseSelfPostPurchaseQuotationReRequestError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    createPurchaseReRequestMutate,
    createPurchaseReRequestIsLoading,
  };
};

/**
 *  구매자 - 자신의 구매 견적 요청서 삭제 Fetch
 *  @function useDeletePurchaseFetch
 */
export const useDeletePurchaseFetch = () => {
  const { mutate: deletePurchaseMutate } = useMutation(
    ['deletePurchase'],
    ({ rfq_number_id }: deletePurchasePayload) => deletePurchaseApi({ rfq_number_id }),
    {
      onError: (err: AxiosError<QuotationPurchaseSelfDeleteError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    deletePurchaseMutate,
  };
};

/**
 *  구매자 - 입찰용 -> 구매용 견적 재요청 Fetch
 *  @function useCreatePurchaseByBidFetch
 */
export const useCreatePurchaseByBidFetch = () => {
  const { mutate: createPurchaseByBidMutate, isLoading: createPurchaseByBidIsLoading } = useMutation(
    ['createPurchaseByBid'],
    ({
      rfq_number_id,
      kind,
      type,
      item_ids,
      quotation_file,
      quotation_file_name,
      project_name,
      end_customer,
      req_close_at,
      is_consultable,
      req_delivery_at,
      restricted_country,
      is_restricted_country,
      terms_of_payment,
      is_loading,
      shipping_area,
      approved_vendor_list,
      approved_vendor_list_file,
      approved_vendor_list_file_name,
      is_approved_vendor_list_file,
      additional_request,
      additional_request_file,
      additional_request_file_name,
      is_additional_request_file,
    }: createPurchaseReRequestPayload) =>
      createPurchaseByBidApi({
        rfq_number_id,
        kind,
        type,
        item_ids,
        quotation_file,
        quotation_file_name,
        project_name,
        end_customer,
        req_close_at,
        is_consultable,
        req_delivery_at,
        restricted_country,
        is_restricted_country,
        terms_of_payment,
        is_loading,
        shipping_area,
        approved_vendor_list,
        approved_vendor_list_file,
        approved_vendor_list_file_name,
        is_approved_vendor_list_file,
        additional_request,
        additional_request_file,
        additional_request_file_name,
        is_additional_request_file,
      }),
    {
      onError: (err: AxiosError<QuotationPurchaseSelfPostPurchaseQuotationByBiddingError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    createPurchaseByBidMutate,
    createPurchaseByBidIsLoading,
  };
};
